import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';

import { useMenuSelection } from 'global/MenuSelectionContext';

interface PageHeaderState {
  title: string;
  subtitle?: string;
}

interface PageHeaderContextType {
  pageHeader: PageHeaderState | null;
}

const PageHeaderContext = createContext<PageHeaderContextType | undefined>(undefined);

export const PageHeaderProvider = ({ children }: { children: ReactNode }) => {
  const { firstLevelMenu, secondLevelMenu, thirdLevelMenu, currentLevel } = useMenuSelection();

  // Derive header from the deepest active menu (Strapi data)
  const pageHeader = useMemo<PageHeaderState | null>(() => {
    const activeMenu =
      currentLevel === 'third' ? (thirdLevelMenu ?? secondLevelMenu) :
      currentLevel === 'second' ? secondLevelMenu :
      firstLevelMenu;

    if (!activeMenu) return null;

    const title = activeMenu.title || activeMenu.label;
    const subtitle =
      activeMenu.subtitle ||
      activeMenu.feature?.description ||
      undefined;

    return { title, subtitle };
  }, [currentLevel, firstLevelMenu, secondLevelMenu, thirdLevelMenu]);

  return (
    <PageHeaderContext.Provider value={{ pageHeader }}>
      {children}
    </PageHeaderContext.Provider>
  );
};

export const usePageHeader = (): PageHeaderContextType => {
  const context = useContext(PageHeaderContext);
  if (!context) {
    throw new Error('usePageHeader must be used within a PageHeaderProvider');
  }
  return context;
};
